import type { HTMLAttributes, ReactNode, Ref } from 'react';
import { Chip } from '../Chip/index.js';

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title' | 'className'> {
  /** Visible h1 content. */
  title: ReactNode;
  /** Secondary copy below the title row. */
  subtitle?: ReactNode;
  /** Pre-formatted count label rendered as a neutral doc-ui Chip. */
  countLabel?: string;
  /** Right-aligned action cluster. */
  actions?: ReactNode;
  /** Optional row above the title for badges/chips. */
  eyebrow?: ReactNode;
  /** Optional breadcrumb row above the title. */
  breadcrumb?: ReactNode;
  /** Optional leading masthead icon. */
  icon?: ReactNode;
  /** Optional compact metadata row below the subtitle. */
  meta?: ReactNode;
  /** id wired to the h1. */
  titleId?: string;
  className?: string;
  ref?: Ref<HTMLElement>;
}

/** Shared page title block for top-level application surfaces. */
export function PageHeader(props: PageHeaderProps): ReactNode {
  const {
    title,
    subtitle,
    countLabel,
    actions,
    eyebrow,
    breadcrumb,
    icon,
    meta,
    titleId,
    className,
    ref,
    ...rest
  } = props;
  const rootClass = ['vq-page-header', className].filter(Boolean).join(' ');

  return (
    <header
      {...rest}
      ref={ref}
      className={rootClass}
      data-component="PageHeader"
      data-has-icon={icon !== undefined ? 'true' : 'false'}
    >
      {breadcrumb !== undefined ? (
        <div className="vq-page-header__breadcrumb">{breadcrumb}</div>
      ) : null}
      <div className="vq-page-header__main">
        {icon !== undefined ? (
          <span className="vq-page-header__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <div className="vq-page-header__content">
          {eyebrow !== undefined ? (
            <div className="vq-page-header__eyebrow">{eyebrow}</div>
          ) : null}
          <div className="vq-page-header__top">
            <h1 id={titleId} className="vq-page-header__title">
              {title}
            </h1>
            {countLabel !== undefined ? (
              <Chip tone="neutral" size="sm">
                {countLabel}
              </Chip>
            ) : null}
            {actions !== undefined ? (
              <div className="vq-page-header__actions">{actions}</div>
            ) : null}
          </div>
          {subtitle !== undefined ? (
            <div className="vq-page-header__subtitle">{subtitle}</div>
          ) : null}
          {meta !== undefined ? (
            <div className="vq-page-header__meta">{meta}</div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
